
import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import { Router } from '@angular/router';
import { Constants } from '../constants';

interface MenuItem {
  title: string;
  link?: string;
  isOpen?: boolean;
  subItems?: SubItem[];
}
interface SubItem {
  title: string;
  link: string;
  icon?: string;
  isOpen?: boolean;
  subItems?: SubItem[];
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  isBrowser: boolean = false;
  isMenuActive: boolean = false;
  private bodyElement: HTMLElement | null = null;
  isSubmenuVisible: boolean = true;
  webUrl = Constants.webURL;
  activeMenu: HTMLElement | null = null;

  menuItems: MenuItem[] = [
    {
      title: 'Services',
      isOpen: false,
      subItems: [
        {
          title: 'Technology Development',
          link: '#',
          isOpen: false,
          subItems: [
            { title: 'Application Development', link: 'application-services', icon: 'fa fa-desktop' },
            { title: 'Mobile Application Development', link: 'mobileservices', icon: 'fa fa-mobile' },
            { title: 'Cloud Computing Services', link: 'cloudservices', icon: 'fa fa-cloud' },
            { title: 'Cyber Security Services', link: 'cybersecurityservices', icon: 'fa-solid fa-shield-halved' },
            { title: 'Database Services', link: 'database-services', icon: 'fa fa-database' },
            { title: 'Operation and Maintenance', link: 'operation-maintanance', icon: 'fa fa-wrench' },
            { title: 'AI / ML Services', link: 'artificial-intelligence', icon: 'fa fa-brain' },
            { title: 'Testing Services', link: 'testing-services', icon: 'fa fa-cogs' }

          ]
        },
        { title: 'Technology Staffing', link: 'technology-staffing', subItems: [] },
        { title: 'Technology Training', link: 'technology-training', subItems: [] },
        { title: 'Digital Marketing', link: 'https://mitdigital360.com/', subItems: [] },
      ]
    },
    {
      title: 'Industries',
      isOpen: false,
      subItems: [
        { title: 'Manufacturing', link: 'manufacturing', subItems: [] },
        { title: 'Finance', link: 'finance', subItems: [] },
        { title: 'Entertainment', link: 'entertainment', subItems: [] },
        { title: 'Retail', link: 'retail', subItems: [] }
      ]
    },
    {
      title: 'About',
      isOpen: false,
      subItems: [
        { title: 'Journey', link: '/journey', subItems: [] },
        // { title: 'Team', link: '/teams', subItems: [] },
        { title: 'Recognition', link: 'recognition', subItems: [] },
        { title: 'Corporate Giving', link: '/corporate-giving', subItems: [] }
      ]
    },
    {
      title: 'Portfolio',
      isOpen: false,
      subItems: [
        { title: 'Case Studies', link: '/case-studies', subItems: [] },
        { title: 'Blogs', link: 'blogs', subItems: [] }
      ]
    },
    {
      title: 'Careers',
      isOpen: false,
      subItems: [
        { title: 'Explore Jobs ', link: '/careers', subItems: [] },
        { title: 'Sparkminds', link: '/internship', subItems: [] }
      ]
    },
    { title: 'Meetups', link: 'mitevents', subItems: [], },
    { title: 'Contact', link: 'contact', subItems: [] }
  ];


  constructor(@Inject(PLATFORM_ID) private platformId: any, private router: Router, private renderer: Renderer2) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.bodyElement = document.body; // Now only accessed if it's browser
      this.setupSubMenuLists();
      this.setupScrollHandler();
      this.setupRemovePreloader();
    }
  }

  // Add cleanup for event listeners
  ngOnDestroy(): void {
    if (this.isBrowser) {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  toggleMobileMenu() {
    this.isMenuActive = !this.isMenuActive;
    this.updateBodyOverflow();
  }

  closeMobileMenu() {
    this.isMenuActive = false;
    this.updateBodyOverflow();
  }
  closeMobileMenuIfOutside(event: MouseEvent) {
    if (this.isBrowser) {
      const targetElement = event.target as HTMLElement;
      if (
        !targetElement.closest('.mobile-menu-overlay__inner') &&
        !targetElement.closest('#mobile-menu-trigger')
      ) {
        this.closeMobileMenu();
      }
    }
  }

  updateBodyOverflow() {
    if (this.bodyElement) {
      if (this.isMenuActive) {
        this.bodyElement.classList.add('no-overflow');
      } else {
        this.bodyElement.classList.remove('no-overflow');
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (
      !targetElement.closest('.mobile-menu-overlay__inner') &&
      !targetElement.closest('#mobile-menu-trigger')
    ) {
      this.closeMobileMenu();
    }
  }

  toggleSubMenu(event: Event, item: MenuItem | SubItem) {
    event.preventDefault(); 
    item.isOpen = !item.isOpen;
  }

  private setupSubMenuLists(): void {
    if (this.isBrowser) {
      const header = document.querySelector('.header') as HTMLElement;
      const subMenuLists = header?.querySelectorAll<HTMLElement>('.has-children .megamenu');

      if (subMenuLists) {
        subMenuLists.forEach((x) => {
          x.style.display = 'none';
          setTimeout(() => {
            x.style.display = '';
          }, 369);
        });
      }
    }
  }

  private setupScrollHandler(): void {
    if (this.isBrowser) {
      window.addEventListener('scroll', this.handleScroll.bind(this));
    }
  }

  private handleScroll(): void {
    if (this.isBrowser) {
      const sticky = document.querySelector('.header-sticky') as HTMLElement;
      const headerHeight = sticky ? sticky.offsetHeight : 0;
      const scroll = window.scrollY || 0;

      if (sticky) {
        if (scroll < headerHeight) {
          sticky.classList.remove('is-sticky');
        } else {
          sticky.classList.add('is-sticky');
        }
      }
    }
  }

  focusMenu(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      const menuItem = event.currentTarget as HTMLElement;
      let subMenu = menuItem.parentElement?.querySelector('.megamenu') as HTMLElement;

      if (subMenu) {
        this.toggleMenu(subMenu);

        subMenu.addEventListener('focusout', () => {
          setTimeout(() => {
            const focusedElement = document.activeElement as HTMLElement;
            if (subMenu && focusedElement && !subMenu.contains(focusedElement)) {
              subMenu.classList.remove('show');
              this.activeMenu = null;
            }
          }, 100);
        });

      } else {
        const subMenu = event.currentTarget as HTMLElement;
        subMenu.classList.remove('show'); // Hide menu when mouse leaves the megamenu itself
        this.activeMenu = null;
      }
    }
  }
  hoverMenu(event: MouseEvent) {
    const menuItem = event.currentTarget as HTMLElement;
    const subMenu = menuItem.parentElement?.querySelector('.megamenu') as HTMLElement;
    if (!subMenu) {
      if (this.activeMenu) {
        this.activeMenu.classList.remove('show');
        this.activeMenu = null;
      }
      return;
    }
    this.toggleMenu(subMenu);
    const headerArea = document.querySelector('.header-area') as HTMLElement;
    headerArea.addEventListener('mouseleave', () => {
      if (this.activeMenu) {
        this.activeMenu.classList.remove('show');
        this.activeMenu = null;
      }
    });
  }

  toggleMenu(subMenu: HTMLElement | null) {
    if (!subMenu) return;

    if (this.activeMenu && this.activeMenu !== subMenu) {
      this.activeMenu.classList.remove('show');
    }
    subMenu.classList.add('show');
    this.activeMenu = subMenu;
  }

  closeMenuOnMouseLeave(event: MouseEvent) {
    const subMenu = event.currentTarget as HTMLElement;
    subMenu.classList.remove('show');
    this.activeMenu = null;
  }
  handleKeydown(event: KeyboardEvent, menu: string) {
    const key = event.key;
    const isEnterOrSpace = key === 'Enter' || key === ' ';

    if (isEnterOrSpace) {
      event.preventDefault();
      if (menu === 'meetups') {
        window.location.href = '/mitevents';
      } else if (menu === 'contact') {
        window.location.href = '/contact';
      }
    }
  }
  closeMenu(event: Event) {
    if (this.isBrowser) {
      const menu = document.querySelector('.newmegamenu.megamenu.megamenu--mega');
      const menuItem = event.currentTarget as HTMLElement;
      const subMenu = menuItem.closest('.has-children')?.querySelector('.megamenu') as HTMLElement;
      if (subMenu && this.activeMenu === subMenu) {
        subMenu.classList.remove('show');
        this.activeMenu = null;
      }
    }
  }
  private setupRemovePreloader(): void {
    if (this.isBrowser) {
      window.addEventListener('load', () => {
        const preloader = document.querySelector('.preloader-activate') as HTMLElement;
        preloader?.classList.remove('preloader-active');
      });

      window.addEventListener('load', () => {
        setTimeout(() => {
          const openPreloader = document.querySelector('.open_tm_preloader') as HTMLElement;
          openPreloader?.classList.add('loaded');
        }, 500);
      });
    }
  }
  slideConfig: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 20,
    loop: true,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    pagination: { clickable: true },
    navigation: true
  };

}
